/**
 * `items.*` — product/service master CRUD + archive.
 *
 * Mirror of `customers.*` (architecture §6.1: parse → call domain → serialize).
 * Item is master data (mutable; UPDATE allowed by RLS) — like customers and
 * unlike invoices. Hard delete impossible because `invoice_lines.item_id`
 * has `onDelete: 'set null'`; archive is the soft-delete seam.
 *
 * V1.0.x field expansion lives here — see ADR-0013 for the rationale.
 *
 * @see docs/scratch/invoices-audit-and-fix-plan.md PR-E — initial sales scope.
 * @see docs/adr/0013-items-field-expansion.md — current shape.
 */
import { z } from "zod";
import { uuid, paged, moneyPaise, cursor } from "../_common.js";

/**
 * Which side of the trade an item appears on:
 *   - `sale`     — sales catalogue; invoice composer typeahead.
 *   - `purchase` — purchase catalogue; bill composer typeahead.
 *   - `both`     — resold inventory; shows in both pickers.
 *
 * Mirrors `items.usage` (migration 0017). Default on create is `sale`
 * (preserves V1.0.x behaviour where every item was implicitly sale-side).
 */
export const itemUsage = z.enum(["sale", "purchase", "both"]);
export type ItemUsage = z.infer<typeof itemUsage>;

/**
 * Physical, intangible, or composite. `bundle` is reserved for V1.x —
 * accepted by the schema so callers can read existing rows, rejected on
 * create/update by the domain (composer disables the radio).
 */
export const itemType = z.enum(["goods", "service", "bundle"]);
export type ItemType = z.infer<typeof itemType>;

/**
 * Tax preference — drives whether the composer prompts for a GST slab.
 *   - `taxable`   — uses the item's defaultTaxRateBps.
 *   - `exempt`    — GST exempt (e.g. fresh milk).
 *   - `nil_rated` — 0% but reported separately.
 *   - `non_gst`   — outside GST (e.g. petrol).
 */
export const taxPreference = z.enum(["taxable", "exempt", "nil_rated", "non_gst"]);
export type TaxPreference = z.infer<typeof taxPreference>;

/**
 * Alternate-unit conversion entries — composer surfaces these in the qty
 * cell of the line table. `factor` is the multiplier from the alt unit to
 * the base `unit`. Example: base = `kg`, alt = `g` with factor 0.001.
 */
export const altUnit = z.object({
  unit: z.string().trim().min(1).max(16),
  factor: z.number().positive(),
});
export const altUnits = z.array(altUnit).max(8);
export type AltUnit = z.infer<typeof altUnit>;

/**
 * The patchable fields on an item master row. Reused by `createInput`
 * (with `name` required) and `updateInput` (`.partial()` — caller patches
 * any subset). New columns land here once.
 */
const baseFields = z.object({
  // ── identity ─────────────────────────────────────────────────────
  /** Display name. Required — drives the picker label and the printed line. */
  name: z.string().trim().min(1, "Name is required").max(255),
  /**
   * Operator-chosen short code (`FAST-M8`). Optional. Unique within the
   * active set per tenant — duplicate against an active row throws
   * `DuplicateSkuError`. Archived rows free their SKU.
   */
  sku: z.string().trim().min(1).max(32).optional(),
  description: z.string().trim().min(1).max(500).optional(),
  /** HSN/SAC. 4-8 chars per GSTN; optional so Tally imports without it land. */
  hsn: z
    .string()
    .trim()
    .regex(/^\d{4,8}$/, "4-8 digit HSN/SAC")
    .optional(),
  /** Unit ('Nos', 'kg', 'lot', 'hr', 'm'). Defaults to 'Nos' on the column. */
  unit: z.string().trim().min(1).max(16).optional(),
  /** Default rate in paise (per `unit`). Stringified bigint on the wire. */
  defaultRatePaise: moneyPaise.optional(),
  /** Default GST rate in basis points (1800 = 18%). */
  defaultTaxRateBps: z.number().int().min(0).max(10_000).optional(),
  /**
   * Which side of the trade this item appears on. Drives which composer's
   * picker surfaces it. Optional on create — column default `'sale'`.
   */
  usage: itemUsage.optional(),

  // ── identity expansion (ADR-0013) ────────────────────────────────
  /** Goods / service / bundle. `'bundle'` rejected by domain in V1.0.x. */
  itemType: itemType.optional(),
  /** Free-text category — filter chip on list. */
  category: z.string().trim().min(1).max(48).optional(),
  brand: z.string().trim().min(1).max(96).optional(),
  manufacturer: z.string().trim().min(1).max(96).optional(),
  barcode: z.string().trim().min(1).max(32).optional(),
  vendorSku: z.string().trim().min(1).max(64).optional(),
  /** Free-form tags. Max 16 to keep the chip strip sane. */
  tags: z.array(z.string().trim().min(1).max(32)).max(16).optional(),

  // ── pricing ──────────────────────────────────────────────────────
  /** Last recorded cost in paise. Margin = (defaultRate - lastCost) / defaultRate. */
  lastCostPaise: moneyPaise.optional(),
  /** MRP in paise. Reserved field. */
  mrpPaise: moneyPaise.optional(),

  // ── tax ──────────────────────────────────────────────────────────
  /** Compensation cess in basis points. */
  cessRateBps: z.number().int().min(0).max(10_000).optional(),
  taxPreference: taxPreference.optional(),
  /** Default TDS section (`194Q`, `194C`, `194J`, …). */
  tdsSectionDefault: z
    .string()
    .trim()
    .regex(/^\d{3}[A-Z]?$/, "3-digit section + optional letter")
    .optional(),

  // ── inventory ────────────────────────────────────────────────────
  altUnits: altUnits.optional(),
  weightGrams: z.number().int().min(0).max(10_000_000).optional(),
  reorderLevel: z.number().int().min(0).optional(),
  trackStock: z.boolean().optional(),

  // ── vendor link ──────────────────────────────────────────────────
  /** Preferred / default vendor for purchases. Pre-fills bill composer. */
  preferredVendorId: uuid.optional(),
  leadTimeDays: z.number().int().min(0).max(3650).optional(),
  minOrderQty: z.number().int().min(0).max(1_000_000).optional(),

  // ── media ────────────────────────────────────────────────────────
  /** S3/Spaces object key for the thumbnail. Set via the upload flow. */
  imageObjectKey: z.string().trim().min(1).max(512).optional(),
});

// ── create ────────────────────────────────────────────────────────
export const createInput = baseFields;
export const createOutput = z.object({ id: uuid });

// ── get one ───────────────────────────────────────────────────────
export const getInput = z.object({ id: uuid });

/**
 * Full item row as returned to the SPA. Optional master fields are
 * `nullable()` (DB-shape) on the wire — the form converts `''` to undefined
 * before round-tripping; the DB stores NULLs.
 */
export const itemRow = z.object({
  id: uuid,
  name: z.string(),
  sku: z.string().nullable(),
  description: z.string().nullable(),
  hsn: z.string().nullable(),
  unit: z.string(),
  defaultRatePaise: z.string().nullable(),
  defaultTaxRateBps: z.number().int().nullable(),
  usage: itemUsage,
  itemType,
  category: z.string().nullable(),
  brand: z.string().nullable(),
  manufacturer: z.string().nullable(),
  barcode: z.string().nullable(),
  vendorSku: z.string().nullable(),
  tags: z.array(z.string()),
  lastCostPaise: z.string().nullable(),
  mrpPaise: z.string().nullable(),
  cessRateBps: z.number().int().nullable(),
  taxPreference,
  tdsSectionDefault: z.string().nullable(),
  altUnits: z.array(altUnit).nullable(),
  weightGrams: z.number().int().nullable(),
  reorderLevel: z.number().int().nullable(),
  trackStock: z.boolean(),
  preferredVendorId: uuid.nullable(),
  /** Hydrated by the read path so the detail page doesn't fetch separately. */
  preferredVendorName: z.string().nullable(),
  leadTimeDays: z.number().int().nullable(),
  minOrderQty: z.number().int().nullable(),
  imageObjectKey: z.string().nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().nullable(),
});

export const getOutput = itemRow;

// ── update ────────────────────────────────────────────────────────
export const updateInput = baseFields.partial().extend({ id: uuid });
export const updateOutput = z.object({ id: uuid });

// ── archive ───────────────────────────────────────────────────────
export const archiveInput = z.object({
  id: uuid,
  reason: z.string().trim().max(255).optional(),
});
export const archiveOutput = z.object({
  id: uuid,
  archivedAt: z.string().datetime(),
});

// ── list ──────────────────────────────────────────────────────────
export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  /** Free-text search: `name` / `sku` / `barcode` / `brand` prefix. Trimmed. */
  q: z.string().optional(),
  /**
   * Tab filter on the list page. Mirrors the prototype's status strip.
   *   - `all`       — every active row (default).
   *   - `goods`     — `item_type = 'goods'`.
   *   - `services`  — `item_type = 'service'`.
   *   - `low_stock` — `reorder_level IS NOT NULL` (V1.0.x: just a flag set).
   *   - `untagged`  — `tags = '{}'` AND `category IS NULL` (cleanup view).
   *   - `archived`  — `archived_at IS NOT NULL`.
   */
  tab: z.enum(["all", "goods", "services", "low_stock", "untagged", "archived"]).default("all"),
  /**
   * Picker filter: which side of the trade the caller is shopping for.
   *   - `sale`     → matches rows with `usage IN ('sale','both')` (invoice composer).
   *   - `purchase` → matches rows with `usage IN ('purchase','both')` (bill composer).
   * Omitted → no usage filter (the standalone /items list page).
   */
  usage: z.enum(["sale", "purchase"]).optional(),
  /** Filter by category chip. */
  category: z.string().trim().min(1).max(48).optional(),
  /** Filter by single tag (V1.0.x — multi-tag intersection lands later). */
  tag: z.string().trim().min(1).max(32).optional(),
  /** Filter by preferred vendor — used for vendor-context drill-in. */
  preferredVendorId: uuid.optional(),
});

/**
 * One row in the list response. Includes the four fields the composer's
 * line typeahead hydrates onto a picked line — `description`, `hsn`,
 * `defaultRatePaise`, `defaultTaxRateBps` — so the picker doesn't need a
 * second fetch on selection. Adds the list-only columns (brand, category,
 * lastCost, margin pill input) for the new list page.
 */
export const listRow = z.object({
  id: uuid,
  name: z.string(),
  sku: z.string().nullable(),
  description: z.string().nullable(),
  hsn: z.string().nullable(),
  unit: z.string(),
  defaultRatePaise: z.string().nullable(),
  defaultTaxRateBps: z.number().int().nullable(),
  usage: itemUsage,
  itemType,
  category: z.string().nullable(),
  brand: z.string().nullable(),
  tags: z.array(z.string()),
  /** Stringified bigint paise. Null = cost not captured yet. */
  lastCostPaise: z.string().nullable(),
  taxPreference,
  trackStock: z.boolean(),
  reorderLevel: z.number().int().nullable(),
  preferredVendorId: uuid.nullable(),
  preferredVendorName: z.string().nullable(),
  imageObjectKey: z.string().nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

export const listOutput = paged(listRow);

// ── activity ─────────────────────────────────────────────────────
/**
 * Per-item audit history. Powers the Activity + Pricing history tabs
 * on the item-detail page. Mirrors `customers.activity` — same shape,
 * same cursor grammar.
 */
export const activityInput = z.object({
  id: uuid,
  limit: z.number().int().min(1).max(100).default(20),
  cursor,
});

export const activityRow = z.object({
  id: uuid,
  /** `item.created` / `item.updated` / `item.archived`. */
  verb: z.string(),
  // Same union as `auditActorType` — these rows come straight from
  // `audit_entries`, so an actor kind this enum omits makes the procedure
  // reject its own data rather than render it.
  actorType: z.enum(["web", "whatsapp", "api", "system", "ai", "buyer"]),
  actorId: z.string(),
  actorName: z.string().nullable(),
  actorRole: z.string().nullable(),
  /**
   * For `updated`: `{ changedKeys: string[], before: Record<string, unknown>,
   * after: Record<string, unknown> }`. The pricing-history tab filters for
   * rows whose `changedKeys` includes `defaultRatePaise` or
   * `defaultTaxRateBps`.
   */
  metadata: z.record(z.string(), z.unknown()),
  at: z.string().datetime(),
});

export const activityOutput = paged(activityRow);

// ── derived TS types (consumed by domain + web) ───────────────────
export type CreateInput = z.infer<typeof createInput>;
export type CreateOutput = z.infer<typeof createOutput>;
export type GetInput = z.infer<typeof getInput>;
export type GetOutput = z.infer<typeof getOutput>;
export type UpdateInput = z.infer<typeof updateInput>;
export type UpdateOutput = z.infer<typeof updateOutput>;
export type ArchiveInput = z.infer<typeof archiveInput>;
export type ArchiveOutput = z.infer<typeof archiveOutput>;
export type ListInput = z.infer<typeof listInput>;
export type ListOutput = z.infer<typeof listOutput>;
export type ListRow = z.infer<typeof listRow>;
export type ItemRow = z.infer<typeof itemRow>;
export type ActivityInput = z.infer<typeof activityInput>;
export type ActivityRow = z.infer<typeof activityRow>;
export type ActivityOutput = z.infer<typeof activityOutput>;
